import type * as React from "react";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  ChevronRight,
  FileText,
  Mail,
  Calendar as CalendarIcon,
  Database,
  Banknote,
  Globe,
  Quote,
  ExternalLink,
} from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from "../primitives/drawer";
import { cn } from "../lib/cn";
import { PDFViewer, type PDFPayload } from "./sources/pdf/PDFViewer";
import {
  buildPDFActions,
  type PDFActionsConfig,
} from "./sources/pdf/PDFActions";
import {
  EmailRender,
  type EmailPayload,
} from "./sources/email/EmailRender";
import { buildEmailActions, type EmailActionsConfig } from "./sources/email/EmailActions";
import {
  EventCard,
  type CalendarEventPayload,
} from "./sources/calendar/EventCard";
import { buildEventActions, type EventActionsConfig } from "./sources/calendar/EventActions";
import { DBRowView, type DBRowPayload } from "./sources/db-row/DBRowView";
import { buildDBActions, type DBActionsConfig } from "./sources/db-row/DBActions";
import {
  TransactionCard,
  type BankingTransactionPayload,
} from "./sources/banking-tx/TransactionCard";
import {
  buildBankingActions,
  type BankingActionsConfig,
} from "./sources/banking-tx/BankingActions";
import { WebPreview, type WebPayload } from "./sources/web/WebPreview";
import {
  formatBreadcrumb,
  formatSourceMetaSubtitle,
  type CitationAnchor,
  type SourceAction,
  type SourceMeta,
  type SourceViewerType,
} from "./sources/types";

/**
 * Per-viewer action configuration union. Apps pass exactly one slot — the
 * matching one for `meta.kind`. Compile-time exhaustiveness is enforced in
 * `defaultActionsForKind`.
 */
export interface SourceActionsConfig {
  pdf?: PDFActionsConfig;
  email?: EmailActionsConfig;
  calendar_event?: EventActionsConfig;
  db_row?: DBActionsConfig;
  banking_transaction?: BankingActionsConfig;
  /** Web viewer has no toolbar actions today — the in-card CTA opens
   *  the URL externally, no other side-effect to chain. Keep the slot
   *  defined so future "Salva pagina" / "Cita anche" extensions stay
   *  parallel to the other viewer families. */
  web?: never;
}

export interface SourceDrawerProps {
  /** Controlled open state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Source metadata + viewer payload. `null` when no source is active. */
  meta: SourceMeta | null;
  /** Per-viewer action handlers — picked by `meta.kind`. */
  actions?: SourceActionsConfig;
  /** Optional extra actions appended to the toolbar (e.g. "Cita anche [n]"). */
  extraActions?: SourceAction[];
  /** Callback when the user clicks the "Apri contesto completo" footer link. */
  onOpenFullContext?: () => void;
  /** Callback when the user clicks the citation excerpt to scroll-to. */
  onScrollToAnchor?: (anchor: CitationAnchor) => void;
}

const ICON_BY_KIND: Record<SourceViewerType, React.ComponentType<{ className?: string }>> = {
  pdf: FileText,
  email: Mail,
  calendar_event: CalendarIcon,
  db_row: Database,
  banking_transaction: Banknote,
  web: Globe,
};

function renderViewer(meta: SourceMeta): React.ReactNode {
  switch (meta.kind) {
    case "pdf":
      return (
        <PDFViewer
          payload={meta.payload as PDFPayload}
          anchor={meta.citation?.anchor}
        />
      );
    case "email":
      return (
        <EmailRender
          payload={meta.payload as EmailPayload}
          anchor={meta.citation?.anchor}
        />
      );
    case "calendar_event":
      return (
        <EventCard
          payload={meta.payload as CalendarEventPayload}
          anchor={meta.citation?.anchor}
        />
      );
    case "db_row":
      return (
        <DBRowView
          payload={meta.payload as DBRowPayload}
          anchor={meta.citation?.anchor}
        />
      );
    case "banking_transaction":
      return (
        <TransactionCard
          payload={meta.payload as BankingTransactionPayload}
          anchor={meta.citation?.anchor}
        />
      );
    case "web":
      return <WebPreview payload={meta.payload as WebPayload} />;
    default: {
      // Exhaustiveness — TS surfaces a "type 'never'" diagnostic if a new
      // viewer kind is added without a case above.
      const _exhaustive: never = meta.kind;
      void _exhaustive;
      return null;
    }
  }
}

function defaultActionsForKind(
  kind: SourceViewerType,
  cfg: SourceActionsConfig | undefined,
  i18n: I18n,
): SourceAction[] {
  if (!cfg) return [];
  switch (kind) {
    case "pdf":
      return cfg.pdf ? buildPDFActions(cfg.pdf, i18n) : [];
    case "email":
      return cfg.email ? buildEmailActions(cfg.email, i18n) : [];
    case "calendar_event":
      return cfg.calendar_event ? buildEventActions(cfg.calendar_event, i18n) : [];
    case "db_row":
      return cfg.db_row ? buildDBActions(cfg.db_row, i18n) : [];
    case "banking_transaction":
      return cfg.banking_transaction
        ? buildBankingActions(cfg.banking_transaction, i18n)
        : [];
    case "web":
      // The in-card CTA inside ``WebPreview`` opens the URL in a new
      // tab — no toolbar actions needed today. Return empty so the
      // toolbar slot collapses cleanly instead of leaving a dead row.
      return [];
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return [];
    }
  }
}

/**
 * SourceDrawer — universal shell for the Source Drawer System (A.11).
 *
 * Five zones (per plan §A.11):
 *   1. Header           — icon + title + meta + close
 *   2. Citation context — chat excerpt + scroll-to anchor
 *   3. Viewer slot      — granular renderer (pdf/email/calendar/db/tx)
 *   4. Actions toolbar  — schema-driven, per-viewer
 *   5. Footer           — origin breadcrumb + "Apri contesto completo →"
 *
 * Replaces v1's `DocumentSidePanel.tsx` (2285 LOC) and `SourcesPanel.tsx`
 * (902 LOC). Each viewer / action set lives under `sources/<kind>/` and is
 * tree-shake-friendly: importing the shell never drags more than the
 * default render switch.
 */
export const SourceDrawer: React.FC<SourceDrawerProps> = ({
  open,
  onOpenChange,
  meta,
  actions,
  extraActions = [],
  onOpenFullContext,
  onScrollToAnchor,
}) => {
  const { i18n } = useLingui();
  if (!meta) {
    // Empty state — render an empty drawer to keep the animation symmetrical
    // (caller toggles `open` independently of `meta`). The ``ariaTitle``
    // prop on ``DrawerContent`` mounts a VisuallyHidden Title — Radix
    // dev-mode quiet, screen readers still get a label.
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent side="right" ariaTitle={i18n._(msg`Fonte`)} />
      </Drawer>
    );
  }

  const Icon = ICON_BY_KIND[meta.kind];
  const actionList = [...defaultActionsForKind(meta.kind, actions, i18n), ...extraActions];

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        className="sm:max-w-xl"
        data-source-kind={meta.kind}
      >
        {/* 1 — Header ─────────────────────────────────────────────────── */}
        <DrawerHeader>
          <div className="flex items-start gap-2.5">
            <span
              className={cn(
                "mt-0.5 grid h-7 w-7 place-items-center rounded-md",
                "bg-ando-orange-soft text-ando-orange",
              )}
              aria-hidden
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <DrawerTitle className="truncate pr-8">{meta.title}</DrawerTitle>
              {(() => {
                const subtitle = formatSourceMetaSubtitle(meta.meta);
                return subtitle ? (
                  <DrawerDescription className="mt-0.5 font-mono text-[11px] tabular-nums">
                    {subtitle}
                  </DrawerDescription>
                ) : null;
              })()}
            </div>
          </div>
        </DrawerHeader>

        {/* 2 — Citation context ───────────────────────────────────────── */}
        {meta.citation ? (
          <button
            type="button"
            onClick={() =>
              meta.citation && onScrollToAnchor?.(meta.citation.anchor)
            }
            className={cn(
              "mx-5 mt-3 flex items-start gap-2 rounded-md border border-hairline",
              "bg-ando-orange-soft/40 px-3 py-2 text-left transition-colors",
              "hover:bg-ando-orange-soft/70 focus-visible:outline-none",
              "focus-visible:ring-2 focus-visible:ring-ando-orange",
            )}
            aria-label={i18n._(msg`Scorri alla zona citata`)}
          >
            <Quote
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ando-orange"
              aria-hidden
            />
            <div className="min-w-0 flex-1 text-ink-2 text-xs">
              <div className="mb-0.5 font-medium text-[10px] text-ando-orange uppercase tracking-wide">
                Citato in chat · [{meta.citation.index}]
              </div>
              <p className="line-clamp-3">{meta.citation.excerpt}</p>
            </div>
          </button>
        ) : null}

        {/* 3 — Viewer slot ─────────────────────────────────────────────── */}
        <DrawerBody>{renderViewer(meta)}</DrawerBody>

        {/* 4 — Actions toolbar ────────────────────────────────────────── */}
        {actionList.length ? (
          <div
            className={cn(
              "flex flex-wrap items-center gap-1.5",
              "border-hairline border-t px-5 py-2.5",
            )}
            role="toolbar"
            aria-label={i18n._(msg`Azioni sulla fonte`)}
          >
            {actionList.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={a.onSelect}
                disabled={a.disabled}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1",
                  "font-medium text-xs transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  a.variant === "primary"
                    ? "bg-ando-orange text-white hover:bg-ando-orange/90"
                    : a.variant === "destructive"
                      ? "border border-hairline bg-canvas text-rose-600 hover:bg-rose-50"
                      : "border border-hairline bg-canvas text-ink-2 hover:bg-surface-1",
                )}
              >
                {a.icon}
                {a.label}
              </button>
            ))}
          </div>
        ) : null}

        {/* 5 — Footer breadcrumb ───────────────────────────────────────── */}
        <DrawerFooter className="flex-row items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-ink-3">
            <span className="font-mono">Da:</span>
            <span className="truncate font-medium text-ink-2">
              {meta.origin.connectorLabel}
            </span>
            <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{formatBreadcrumb(meta.origin.breadcrumb)}</span>
          </div>
          {onOpenFullContext || meta.origin.href ? (
            <button
              type="button"
              onClick={() => {
                if (onOpenFullContext) {
                  onOpenFullContext();
                } else if (meta.origin.href) {
                  window.open(meta.origin.href, "_blank", "noopener,noreferrer");
                }
              }}
              className={cn(
                "inline-flex items-center gap-1 font-medium text-[11px]",
                "text-ando-orange hover:underline",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
              )}
            >
              {i18n._(msg`Apri contesto completo`)}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
SourceDrawer.displayName = "SourceDrawer";
